import { useEffect } from 'react'
import { type TimerState, parseTimerState } from '../../lib/timer'

export const TIMER_KEY = 'focus-cafe:timer'

const CHIME_SRC = '/sounds/zihou.mp3'
/** 時報の最後の長い「プー」が鳴り始める位置。ここが残り 0 秒に重なるように前倒しで鳴らす */
const CHIME_LEAD_MS = 3160

let chime: HTMLAudioElement | null = null
let pending: number | undefined
let scheduledEnd: number | null = null

/**
 * 開始ボタンを押したときに読み込んでおき、終わった瞬間に待たずに鳴らせるようにする。
 * Safari と iOS はクリックの中で一度再生した要素でないと後から鳴らせないので、
 * ここで無音のまま再生してすぐ止めておく
 */
export function prepareChime() {
  try {
    chime ??= new Audio(CHIME_SRC)
    // 鳴っている途中なら止めない
    if (!chime.paused) return
    chime.preload = 'auto'
    const a = chime
    a.muted = true
    a.play()
      .then(() => {
        a.pause()
        a.currentTime = 0
      })
      .catch(() => {})
      .finally(() => {
        a.muted = false
      })
  } catch {
    // Audio が使えない環境では鳴らさない
  }
}

/** offsetMs だけ頭を飛ばして鳴らす。鳴らし始めが遅れても最後の音は 0 秒に合う */
function playChime(offsetMs: number) {
  try {
    chime ??= new Audio(CHIME_SRC)
    chime.muted = false
    chime.currentTime = offsetMs / 1000
    // 自動再生が許されていない環境では鳴らないだけにする
    chime.play().catch(() => {})
  } catch {
    // Audio が使えない環境では鳴らさない
  }
}

export function stopChime() {
  if (!chime) return
  chime.pause()
  chime.currentTime = 0
}

/**
 * 集中や休憩の終わりに合わせて時報を予約する。null で取り消す。
 * 予約はページをまたいで残したいので、コンポーネントではなくモジュールに一つだけ持つ。
 * 終わったあとに開き直したときは鳴らさない
 */
export function scheduleChime(end: number | null) {
  if (end === scheduledEnd) return
  window.clearTimeout(pending)
  pending = undefined
  scheduledEnd = end
  if (end === null) return
  pending = window.setTimeout(
    () => {
      pending = undefined
      const late = Date.now() - (end - CHIME_LEAD_MS)
      if (late < CHIME_LEAD_MS) playChime(Math.max(0, late))
    },
    end - CHIME_LEAD_MS - Date.now(),
  )
}

/** 時報を鳴らす時刻。集中か休憩の途中なら、その終わり */
export function chimeAtOf(s: TimerState): number | null {
  return s.phase === 'focus' || s.phase === 'rest' ? s.startedAt + s.plannedMinutes * 60_000 : null
}

export function loadTimer(): unknown {
  try {
    const raw = localStorage.getItem(TIMER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    // 保存できない環境では毎回 idle から
    return null
  }
}

function scheduleFromStorage() {
  const s = parseTimerState(loadTimer())
  scheduleChime(s ? chimeAtOf(s) : null)
}

/**
 * どのページにいても集中や休憩の終わりに時報を鳴らす。店以外のページでリロードしても予約し直す。
 * リロードのあとはクリックするまで音を出せないので、最初の操作で鳴らせる状態にしておく
 */
export function useFocusChime() {
  useEffect(() => {
    scheduleFromStorage()
    const unlock = () => {
      if (scheduledEnd !== null) prepareChime()
    }
    // 別のタブで始めたりやめたりしたときも合わせる
    const onStorage = (e: StorageEvent) => {
      if (e.key === TIMER_KEY) scheduleFromStorage()
    }
    window.addEventListener('pointerdown', unlock, { once: true, capture: true })
    window.addEventListener('keydown', unlock, { once: true, capture: true })
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('pointerdown', unlock, { capture: true })
      window.removeEventListener('keydown', unlock, { capture: true })
      window.removeEventListener('storage', onStorage)
    }
  }, [])
}
